/**
 * Main-device screen for the "share this game" feature — shows the guest
 * link generated for the current game and lets the operator copy it. The
 * guest side of this feature is `guestPlay.ts`; the relay-facing plumbing is
 * `src/ui/gameShare.ts`.
 */

import { renderBrandHeader } from '../branding';
import { esc } from '../helpers';
import qrcodeFactory from '../vendor/qrcodeGen';

export interface ShareGameActions {
  back: () => void;
  /** Kills the current guest link and mints a fresh one — see relay/gameShare.ts's `regenerateShareToken`. */
  regenerate: () => void;
}

/**
 * Error-correction level 'M' (recovers ~15% damage) is the standard default
 * for a QR meant to be read off a phone screen at close range, rather than
 * 'L' (skimps on recovery to save a little space, not needed here — the
 * guest link is short) or 'H' (only worth it printed/handled, e.g. stuck to
 * a table where it could get smudged).
 */
export function renderGuestLinkQr(guestLink: string): string {
  const qr = qrcodeFactory(0, 'M');
  qr.addData(guestLink);
  qr.make();
  return qr.createSvgTag({ cellSize: 5, margin: 12 });
}

export function renderShareGame(
  root: HTMLElement,
  guestLink: string,
  actions: ShareGameActions,
  /** Set after a failed regenerate attempt — the old link is still live in this case, nothing was lost. */
  error?: string,
): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Share This Game</h1>
      <p class="muted">
        Send this link to a player without the main device. They can record a round they win — take or upload
        a photo, arrange the hand, and tap Record Round — but they can't edit the last round or end the game.
        Only this device can do that.
      </p>
      <p class="muted">
        The link stops working 1 hour after you end the game — tell guests to download their PDF before then.
      </p>

      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}

      <div style="display:flex;justify-content:center;margin:1rem 0">
        ${renderGuestLinkQr(guestLink)}
      </div>
      <p class="muted" style="text-align:center;margin-top:-0.5rem">Scan with their phone's camera to open it directly.</p>

      <label for="guestLinkField">Guest link</label>
      <input type="text" id="guestLinkField" readonly value="${esc(guestLink)}" style="width:100%" />

      <div class="row" style="margin-top:0.5rem">
        <button type="button" id="copyLinkBtn">Copy Link</button>
      </div>
      <p id="copyNote" class="muted" style="display:none;margin-top:0.3rem">Copied.</p>

      <button type="button" id="regenerateLinkBtn" class="ghost" style="width:100%;margin-top:1rem">
        Wrong person got the link? Generate a new one
      </button>
      <div id="regenerateConfirm"></div>

      <button type="button" id="shareBackBtn" class="secondary" style="width:100%;margin-top:1.5rem">
        ← Back to Game
      </button>
    </div>
  `;

  const field = root.querySelector<HTMLInputElement>('#guestLinkField')!;
  const copyNote = root.querySelector<HTMLParagraphElement>('#copyNote')!;

  root.querySelector<HTMLButtonElement>('#copyLinkBtn')!.addEventListener('click', () => {
    field.select();
    void navigator.clipboard
      ?.writeText(guestLink)
      .then(() => {
        copyNote.style.display = '';
      })
      .catch(() => {
        // Clipboard permission can be denied — the field is still selected
        // for a manual copy, so this isn't a dead end.
      });
  });

  const regenerateBtn = root.querySelector<HTMLButtonElement>('#regenerateLinkBtn')!;
  const regenerateConfirm = root.querySelector<HTMLDivElement>('#regenerateConfirm')!;
  regenerateBtn.addEventListener('click', () => {
    regenerateBtn.disabled = true;
    regenerateConfirm.innerHTML = `
      <div class="card" style="margin-top:0.5rem">
        <p><strong>Generate a new link?</strong></p>
        <p class="muted">The current link stops working immediately — anyone still holding it (including the wrong
        person) won't be able to submit rounds anymore. This device keeps everything already recorded.</p>
        <div class="row">
          <button type="button" id="regenerateCancelBtn" class="secondary">Cancel</button>
          <button type="button" id="regenerateConfirmBtn" class="danger">Yes, Generate New Link</button>
        </div>
      </div>
    `;
    regenerateConfirm.querySelector<HTMLButtonElement>('#regenerateCancelBtn')!.addEventListener('click', () => {
      regenerateConfirm.innerHTML = '';
      regenerateBtn.disabled = false;
    });
    regenerateConfirm.querySelector<HTMLButtonElement>('#regenerateConfirmBtn')!.addEventListener('click', () => {
      actions.regenerate();
    });
  });

  root.querySelector<HTMLButtonElement>('#shareBackBtn')!.addEventListener('click', () => {
    actions.back();
  });
}
